// src/hooks/useSessionValidator.js
// Hook to detect when user returns after being away and validate session

import { useEffect, useRef, useCallback } from 'react';
import { useUser } from '../context/UserContext';
import { getAuth } from 'firebase/auth';
import {
    updateLastActivity,
    hasUserBeenAway,
    markUserAway,
    wasUserAway,
    isPageRefresh,
    debounce
} from '../utils/sessionUtils';

const useSessionValidator = () => {
    const { currentUser, setError } = useUser();
    const auth = getAuth();
    const validationInProgressRef = useRef(false);
    const awayTimeThresholdRef = useRef(10); // minutes
    const hasInitializedRef = useRef(false);

    // Debounced activity update to prevent excessive localStorage writes
    const debouncedUpdateActivity = useCallback(
        debounce(() => {
            updateLastActivity();
        }, 1000),
        []
    );

    // Validate current session
    const validateSession = useCallback(async () => {
        if (!currentUser || validationInProgressRef.current) {
            return true; // No user or already validating
        }

        try {
            validationInProgressRef.current = true;
            // console.log('🔍 Validating user session...');

            // Force token refresh to check if it's still valid
            await currentUser.getIdToken(true);

            // If we get here, token is still valid
            // console.log('✅ Session validation successful');
            updateLastActivity();
            return true;

        } catch (error) {
            // console.warn('❌ Session validation failed:', error.message);

            // Clear any existing errors first
            if (setError) setError(null);

            // Show a user-friendly message
            // console.log('🔄 Session expired. Refreshing page...');

            // Sign out to clear any invalid state
            try {
                await auth.signOut();
            } catch (signOutError) {
                console.error('Error during sign out:', signOutError);
            }

            // Use setTimeout to allow the sign out to complete
            setTimeout(() => {
                window.location.reload();
            }, 500);

            return false;
        } finally {
            validationInProgressRef.current = false;
        }
    }, [currentUser, auth, setError]);

    // Handle page visibility change
    const handleVisibilityChange = useCallback(async () => {
        if (document.hidden) {
            // Page is being hidden - mark user as away
            markUserAway();
            // console.log('👋 User navigated away from page');
            return;
        }

        // Page is visible again
        // console.log('👁️ User returned to page');

        // Check if user was away and for how long
        const awayStartTime = wasUserAway();
        if (awayStartTime) {
            const awayDuration = (Date.now() - awayStartTime) / 1000 / 60; // minutes
            // console.log(`⏰ User was away for ${Math.round(awayDuration)} minutes`);

            if (awayDuration > awayTimeThresholdRef.current) {
                await validateSession();
            }
        }

        // Also check using stored last activity as backup
        if (hasUserBeenAway(awayTimeThresholdRef.current)) {
            // console.log(`⚠️ Extended inactivity detected (>${awayTimeThresholdRef.current}m)`);
            await validateSession();
        }

        // Update activity
        updateLastActivity();
    }, [validateSession]);

    // Handle focus events (user switches back to tab)
    const handleFocus = useCallback(async () => {
        // Only check if user has been away for the threshold time
        if (hasUserBeenAway(awayTimeThresholdRef.current)) {
            // console.log('🎯 Tab focused after extended away time');
            await validateSession();
        }

        updateLastActivity();
    }, [validateSession]);

    // Track user interactions to update last activity
    const handleUserInteraction = useCallback(() => {
        debouncedUpdateActivity();
    }, [debouncedUpdateActivity]);

    // Initialize and set up event listeners
    useEffect(() => {
        // Initialize session tracking on mount
        if (!hasInitializedRef.current && !isPageRefresh()) {
            // console.log('🚀 Session validator initialized');
            updateLastActivity();
            hasInitializedRef.current = true;
        }

        // Set up event listeners
        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('focus', handleFocus);

        // User interaction events (debounced to avoid excessive calls)
        const interactionEvents = ['click', 'keydown', 'scroll', 'touchstart', 'mousemove'];
        interactionEvents.forEach(event => {
            document.addEventListener(event, handleUserInteraction, { passive: true });
        });

        // Cleanup function
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('focus', handleFocus);

            interactionEvents.forEach(event => {
                document.removeEventListener(event, handleUserInteraction);
            });
        };
    }, [handleVisibilityChange, handleFocus, handleUserInteraction]);

    // Return validation function for manual use if needed
    return {
        validateSession,
        updateLastActivity,
        setAwayTimeThreshold: (minutes) => {
            awayTimeThresholdRef.current = minutes;
            // console.log(`⚙️ Away time threshold set to ${minutes} minutes`);
        },
        hasUserBeenAway: () => hasUserBeenAway(awayTimeThresholdRef.current)
    };
};

export default useSessionValidator;